import type { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { BARRICADE, type ArenaState, type BarricadeState } from '@arena/shared';

/** Real world aligned scenery, so the visible wall matches its collision box. */
export class BarricadesView {
  private readonly views=new Map<string,{root:Mesh;material:StandardMaterial;age:number}>();
  private readonly texture:Texture;
  private readonly sideTexture:Texture;
  private readonly scene:Scene;
  constructor(scene:Scene){
    this.scene=scene;
    this.texture=new Texture('/proto2d/mussolini/barricade.png',scene,true,true,Texture.NEAREST_SAMPLINGMODE);
    this.texture.hasAlpha=true;
    this.sideTexture=new Texture('/proto2d/mussolini/barricade-side.png',scene,true,true,Texture.NEAREST_SAMPLINGMODE);
  }
  private create(b:BarricadeState){
    const root=new Mesh('barricade',this.scene);
    root.position.set(b.x,b.y,b.z);root.rotation.y=b.alongX?0:Math.PI/2;
    const material=new StandardMaterial('barricade-art',this.scene);
    material.diffuseTexture=this.texture;material.useAlphaFromDiffuseTexture=true;
    material.disableLighting=true;material.emissiveColor=new Color3(.78,.75,.68);material.specularColor=Color3.Black();material.backFaceCulling=false;
    for(const depth of [-BARRICADE.depth/2,BARRICADE.depth/2]){
      const face=CreatePlane('sandbags',{width:BARRICADE.width,height:BARRICADE.height},this.scene);
      face.parent=root;face.position.set(0,BARRICADE.height/2,depth);face.material=material;face.isPickable=false;
    }
    const side=new StandardMaterial('sandbag-sides',this.scene);
    side.diffuseTexture=this.sideTexture;side.disableLighting=true;
    side.emissiveColor=new Color3(.7,.67,.58);side.specularColor=Color3.Black();
    for(let row=0;row<3;row++)for(let col=0;col<4;col++){
      const bag=CreateCylinder('sandbag-volume',{height:.77,diameter:.55,tessellation:8},this.scene);
      bag.parent=root;bag.rotation.z=Math.PI/2;bag.scaling.x=.85;
      bag.position.set((col-1.5)*.77+(row===1?.08:0),.23+row*.44,0);
      bag.material=side;bag.isPickable=false;
    }
    root.metadata={side};
    return {root,material,age:0};
  }
  update(state:ArenaState,dt:number){
    for(const [id,b]of state.barricades){
      let v=this.views.get(id);if(!v){v=this.create(b);this.views.set(id,v);}
      v.age+=dt;
      v.root.scaling.y=Math.min(1,.15+v.age*5);
      const tint=.55+.45*b.hp/BARRICADE.hp;v.material.diffuseColor.set(tint,tint,tint);
      v.root.setEnabled(b.ticks>30 || Math.floor(b.ticks/4)%2===0);
    }
    for(const [id,v]of this.views)if(!state.barricades.has(id)){
      v.root.metadata.side.dispose();v.root.dispose();v.material.dispose();this.views.delete(id);
    }
  }
  clear(){for(const v of this.views.values()){v.root.metadata.side.dispose();v.root.dispose();v.material.dispose();}this.views.clear();}
}
